import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Sparkles, Film, Image as ImageIcon, Trash2, Download, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const VIDEO_KINDS = [
  { id: "reel", label: "Instagram Reel" },
  { id: "youtube_short", label: "YouTube Short" },
  { id: "brand_intro", label: "Brand Intro" },
  { id: "promo", label: "Service Promo" },
  { id: "festival_video", label: "Festival Video" },
  { id: "explainer", label: "Explainer" },
];
const CREATIVE_KINDS = [
  { id: "ad_creative", label: "Ad Creative" },
  { id: "festival_post", label: "Festival Post" },
  { id: "story_banner", label: "Story Banner" },
  { id: "offer_poster", label: "Offer Poster" },
];

export default function VideoStudio() {
  const [tab, setTab] = useState("script");
  const [videoKind, setVideoKind] = useState("reel");
  const [creativeKind, setCreativeKind] = useState("ad_creative");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("Bold");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const token = localStorage.getItem("token");
  const imgSrc = (it) => {
    if (it.storage_path) return `${API}/files/${it.storage_path}?auth=${encodeURIComponent(token)}`;
    if (it.image_b64) return `data:image/png;base64,${it.image_b64}`;
    return "";
  };

  const load = async () => {
    const { data } = await api.get("/video/list");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const generate = async () => {
    if (!topic.trim()) {
      toast.error("Enter a topic");
      return;
    }
    setBusy(true);
    try {
      if (tab === "script") {
        const { data } = await api.post("/video/script", { kind: videoKind, topic, tone });
        setItems([data, ...items]);
        toast.success("Video script ready");
      } else {
        const { data } = await api.post("/video/creative", { kind: creativeKind, prompt_hint: topic });
        setItems([data, ...items]);
        toast.success("Creative generated");
      }
      setTopic("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/video/${id}`);
    setItems(items.filter((x) => x.id !== id));
  };

  return (
    <div className="space-y-8" data-testid="video-studio-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">Video & Creative Studio</div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Reels, ads & festival posts.</h1>
        <p className="text-white/50 mt-2 max-w-2xl text-sm">
          AI scripts reels, shorts, brand intros & promos. Image creatives (ads, festival, story, offer) come ready to publish. Video rendering coming soon — storyboards ship now.
        </p>
      </header>

      <div className="glass rounded-2xl p-6 lg:p-8">
        <div className="flex gap-2 mb-5">
          <button
            onClick={() => setTab("script")}
            data-testid="video-tab-script"
            className={`px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 ${tab === "script" ? "brand-gradient" : "glass hover:bg-white/5"}`}
          >
            <Film className="h-4 w-4" /> Video Script
          </button>
          <button
            onClick={() => setTab("creative")}
            data-testid="video-tab-creative"
            className={`px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 ${tab === "creative" ? "brand-gradient" : "glass hover:bg-white/5"}`}
          >
            <ImageIcon className="h-4 w-4" /> Image Creative
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Type</label>
            {tab === "script" ? (
              <select
                value={videoKind}
                data-testid="video-kind-select"
                onChange={(e) => setVideoKind(e.target.value)}
                className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm"
              >
                {VIDEO_KINDS.map((k) => (
                  <option key={k.id} value={k.id}>{k.label}</option>
                ))}
              </select>
            ) : (
              <select
                value={creativeKind}
                data-testid="creative-kind-select"
                onChange={(e) => setCreativeKind(e.target.value)}
                className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm"
              >
                {CREATIVE_KINDS.map((k) => (
                  <option key={k.id} value={k.id}>{k.label}</option>
                ))}
              </select>
            )}
          </div>
          {tab === "script" && (
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Tone</label>
              <select value={tone} onChange={(e) => setTone(e.target.value)} className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm">
                {["Bold", "Friendly", "Professional", "Playful", "Inspirational", "Luxury"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          )}
          <div className={tab === "script" ? "" : "md:col-span-2"}>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Topic / brief</label>
            <input
              value={topic}
              data-testid="video-topic-input"
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. Launching the new sustainable summer collection"
              className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm"
            />
          </div>
        </div>

        <button
          onClick={generate}
          data-testid="video-generate-button"
          disabled={busy}
          className="mt-5 inline-flex items-center gap-2 brand-gradient px-6 py-3 rounded-lg text-sm font-semibold hover:opacity-90 glow-orange disabled:opacity-50"
        >
          <Sparkles className="h-4 w-4" /> {busy ? "Generating…" : tab === "script" ? "Generate script" : "Generate creative"}
        </button>
      </div>

      <section>
        <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">Your assets</div>
        {items.length === 0 ? (
          <div className="glass rounded-2xl p-10 text-center text-white/50 text-sm">No assets yet — generate one above.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((it) => (
              <div key={it.id} className="glass rounded-2xl p-6" data-testid={`video-item-${it.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full glass">{it.kind}</span>
                  <button onClick={() => del(it.id)} className="p-1.5 rounded-lg hover:bg-white/5">
                    <Trash2 className="h-4 w-4 text-white/50" />
                  </button>
                </div>

                {it.asset_type === "creative_image" ? (
                  <div className="bg-white rounded-lg overflow-hidden">
                    <img src={imgSrc(it)} alt="Creative" className="w-full" />
                  </div>
                ) : (
                  <div>
                    <h3 className="font-heading font-bold text-xl mb-2">{it.script?.title}</h3>
                    <div className="text-xs text-white/40 flex items-center gap-1.5 mb-3 font-mono">
                      <Clock className="h-3 w-3" /> {it.script?.duration_seconds}s · {it.script?.music_mood}
                    </div>
                    <div className="text-xs text-orange-400 mb-1 uppercase tracking-[0.2em]">Hook</div>
                    <p className="text-sm text-white/85 mb-3">"{it.script?.hook}"</p>
                    <div className="space-y-2 text-sm">
                      {(it.script?.scenes || []).map((s, i) => (
                        <div key={i} className="border border-white/5 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-mono text-orange-400">{s.timestamp}</span>
                          </div>
                          <div className="text-xs text-white/60 italic">{s.visual}</div>
                          <div className="text-sm text-white/85 mt-1">VO: {s.voiceover}</div>
                          {s.on_screen_text && <div className="text-xs text-white/50 mt-1">Text: "{s.on_screen_text}"</div>}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 brand-gradient inline-block px-3 py-1.5 rounded-lg text-xs font-semibold">{it.script?.cta}</div>
                    <div className="mt-3 text-xs text-white/50">{it.script?.caption}</div>
                    <div className="mt-2 text-xs text-orange-400 font-mono">
                      {(it.script?.hashtags || []).map((h) => `#${String(h).replace(/^#/, "")}`).join(" ")}
                    </div>
                    {it.video_url ? (
                      <a href={it.video_url} className="mt-3 text-xs text-orange-400 inline-flex items-center gap-1">
                        <Download className="h-3 w-3" /> Download
                      </a>
                    ) : it.video_storage_path ? (
                      <a
                        href={`${API}/files/${it.video_storage_path}?auth=${encodeURIComponent(token)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 text-xs text-orange-400 inline-flex items-center gap-1"
                      >
                        <Download className="h-3 w-3" /> Open rendered video
                      </a>
                    ) : (
                      <RenderButton
                        scriptId={it.id}
                        onRendered={() => { /* user can refresh manually */ }}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function RenderButton({ scriptId, onRendered }) {
  const [status, setStatus] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showOpts, setShowOpts] = useState(false);
  const [size, setSize] = useState("1024x1792");
  const [duration, setDuration] = useState(4);

  const startRender = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/video/render", { script_id: scriptId, model: "sora-2", size, duration });
      setJobId(data.id);
      setStatus(data.status);
      toast.success(`Sora 2 rendering started · ${size} · ${duration}s — 2-5 min`);
      poll(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Render failed");
      setBusy(false);
    }
  };

  const poll = async (id) => {
    const tick = async () => {
      try {
        const { data } = await api.get(`/video/render/${id}`);
        setStatus(data.status);
        if (data.status === "done") {
          toast.success("Video ready");
          onRendered?.();
        } else if (data.status === "failed") {
          toast.error("Render failed: " + (data.error || ""));
        } else {
          setTimeout(tick, 12000);
        }
      } catch {}
    };
    setTimeout(tick, 8000);
  };

  if (status === "done") {
    return (
      <div className="mt-3 text-xs text-green-400 inline-flex items-center gap-1 font-mono uppercase tracking-[0.2em]">
        ✓ Rendered · refresh to view
      </div>
    );
  }
  if (jobId && status && status !== "failed") {
    return (
      <div className="mt-3 text-xs text-orange-400 inline-flex items-center gap-1.5">
        <Loader2 className="h-3 w-3 animate-spin" /> Sora 2 rendering · {status}…
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-2">
      {showOpts && (
        <div className="flex gap-2 flex-wrap">
          <select value={size} onChange={(e) => setSize(e.target.value)} className="text-xs glass px-2 py-1.5 rounded-lg bg-transparent border border-white/10">
            <option value="1024x1792">Vertical 1024×1792 (Reel/Short)</option>
            <option value="1792x1024">Landscape 1792×1024</option>
            <option value="1280x720">HD 1280×720</option>
            <option value="1024x1024">Square 1024×1024</option>
          </select>
          <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="text-xs glass px-2 py-1.5 rounded-lg bg-transparent border border-white/10">
            <option value={4}>4 seconds</option>
            <option value={8}>8 seconds</option>
            <option value={12}>12 seconds</option>
          </select>
        </div>
      )}
      <div className="flex gap-2">
        <button
          onClick={startRender}
          data-testid={`sora-render-${scriptId}`}
          disabled={busy}
          className="text-xs glass px-3 py-1.5 rounded-lg hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <Film className="h-3 w-3 text-orange-400" /> {busy ? "Starting…" : `Render with Sora 2 (${duration}s)`}
        </button>
        <button
          onClick={() => setShowOpts(!showOpts)}
          data-testid={`sora-opts-${scriptId}`}
          className="text-xs glass px-3 py-1.5 rounded-lg hover:bg-white/5 text-white/60"
        >
          {showOpts ? "Hide options" : "Options"}
        </button>
      </div>
    </div>
  );
}
